'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { AddressBook, Edit, Search as SearchIcon } from '@drift-labs/icons';
import { useRouter } from 'next/navigation';
import {
	Root as PopoverRoot,
	PopoverTrigger,
	PopoverAnchor,
	PopoverPortal,
	PopoverContent,
} from '@radix-ui/react-popover';
import MarketIcon from 'src/components/Utils/MarketIcon';
import DropdownUi from 'src/components/primitives/DropdownUi/DropdownUi';
import Input from 'src/components/primitives/Input/Input';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { DROPDOWN_SIDE_OFFSET } from 'src/components/primitives/DropdownUi/consts';
import {
	type WhitelistRecord,
	useWhitelistAddresses,
} from 'src/hooks/query/useWhitelistAddresses';
import { getChainIconSymbol, CHAIN_NAME_MAP } from 'src/constants/funXyzChains';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import useApiAuth from 'src/hooks/useApiAuth';
import useModal from 'src/components/Modals/hooks/useModal';
import Button from 'src/components/primitives/Button/Button';
import { abbreviateAddress } from '@drift/common';
import InlineLoadingBar from 'src/components/InlineLoadingBar/InlineLoadingBar';

interface AddressBookSearchDropdownProps {
	onSelect: (record: WhitelistRecord) => void;
	children: React.ReactNode;
}

export default function AddressBookSearchDropdown({
	onSelect,
	children,
}: AddressBookSearchDropdownProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const router = useRouter();
	const handleCloseDrawer = useDrawersStore((s) => s.handleCloseDrawer);
	const { data: addresses = [], isLoading } = useWhitelistAddresses(open);
	const { needsSignlessSetup } = useApiAuth();
	const { push } = useModal();

	const filtered = useMemo(() => {
		if (!search) return addresses;
		const term = search.toLowerCase();
		return addresses.filter(
			(a) =>
				a.label.toLowerCase().includes(term) ||
				a.address.toLowerCase().includes(term) ||
				(CHAIN_NAME_MAP[a.chainId] ?? '').toLowerCase().includes(term)
		);
	}, [addresses, search]);

	const handleSelect = useCallback(
		(record: WhitelistRecord) => {
			onSelect(record);
			setOpen(false);
			setSearch('');
		},
		[onSelect]
	);

	const handleEditClick = useCallback(() => {
		handleCloseDrawer('accounts');
		router.push('/address-book');
		setOpen(false);
	}, [handleCloseDrawer, router]);

	return (
		<PopoverRoot open={open} onOpenChange={setOpen}>
			<PopoverAnchor>{children}</PopoverAnchor>
			<PopoverPortal>
				<PopoverContent asChild sideOffset={DROPDOWN_SIDE_OFFSET} align="start">
					<DropdownUi.Content className="w-(--radix-popover-trigger-width)">
						<div className="px-lg py-md">
							<Input
								id="address-book-search"
								type="text"
								size="sm"
								labelVariant="inset"
								label={
									<>
										<SearchIcon size={16} />
										<span className="sr-only">Search</span>
									</>
								}
								labelClassName="flex transition-colors pr-xs"
								inputClassName="text-left pl-0"
								value={search}
								onChange={setSearch}
								placeholder="Search for Address, Label, Chain"
								suffix={
									<IconButton
										size="sm"
										variant="tertiary"
										className="-m-sm p-sm"
										icon={Edit}
										onClick={handleEditClick}
										title="Manage Address Book"
									/>
								}
							/>
						</div>

						<div className="hidden-scroll max-h-[240px] overflow-y-auto">
							{needsSignlessSetup ? (
								<div className="flex items-center justify-center py-8">
									<Button
										variant="secondary"
										size="sm"
										onClick={() =>
											push({
												type: 'enable_signless_trading',
												props: { variant: 'address_book' },
											})
										}
									>
										Authenticate
									</Button>
								</div>
							) : isLoading ? (
								<div className="flex items-center justify-center py-8">
									<InlineLoadingBar />
								</div>
							) : filtered.length === 0 ? (
								<div className="flex items-center justify-center py-8 typo-b3 text-text-secondary">
									No records found
								</div>
							) : (
								filtered.map((record) => (
									<button
										type="button"
										key={record.whitelistId}
										onClick={() => handleSelect(record)}
										className="w-full"
									>
										<DropdownUi.Item className="flex-col items-start gap-y-sm">
											<div className="flex w-full justify-between gap-x-lg">
												<span className="truncate typo-t3">{record.label}</span>

												<div className="flex shrink-0 items-center gap-x-xs">
													<MarketIcon
														marketSymbol={
															getChainIconSymbol(record.chainId) ?? ''
														}
														className="size-lg"
													/>
													<span className="typo-t3">
														{CHAIN_NAME_MAP[record.chainId] ?? record.chainId}
													</span>
												</div>
											</div>

											<span className="text-text-secondary">
												{abbreviateAddress(record.address, 6)}
											</span>
										</DropdownUi.Item>
									</button>
								))
							)}
						</div>
					</DropdownUi.Content>
				</PopoverContent>
			</PopoverPortal>
		</PopoverRoot>
	);
}

export function AddressBookTrigger() {
	return (
		<PopoverTrigger asChild>
			<IconButton
				variant="tertiary"
				size="md"
				title="Address Book"
				aria-label="Open Address Book"
				icon={AddressBook}
			/>
		</PopoverTrigger>
	);
}
